import { useState } from 'react';
import { Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();
      if (data.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        setError('Error sending message. Please try again.');
      }
    } catch {
      setError('Connection error. Check your internet and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="container mx-auto px-4 py-8 sm:py-16 max-w-5xl">
        <div className="mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-4">
            Get in Touch
          </h2>
          <p className="text-center text-gray-600 mb-8">
            We have a dedicated team ready to help you. We'll respond as quickly as possible.
          </p>
        </div>

        <div className="flex justify-center mb-12">
          <a href="mailto:growproplus@growproplus.com" className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-100 flex flex-col items-center text-center hover:shadow-md hover:scale-[1.02] transition-all cursor-pointer max-w-xs w-full">
            <div className="w-12 h-12 bg-purple-600 text-white rounded-xl flex items-center justify-center mb-4">
              <Mail className="w-6 h-6" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Email</h3>
            <p className="text-gray-600 text-sm">growproplus@growproplus.com</p>
            <p className="text-xs text-gray-500 mt-2">Reply within 24 hours</p>
          </a>
        </div>

        {/* Contact Form */}
        <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 sm:p-8 shadow-lg max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Send a Message</h3>

          {submitted && (
            <div className="bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded-xl mb-6">
              ✓ Message sent successfully! We'll get back to you soon.
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-xl mb-6">
              ✗ {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input
                type="text" name="name" value={formData.name} onChange={handleChange} required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-pink-600 focus:ring-2 focus:ring-pink-100 transition"
                placeholder="Your name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email" name="email" value={formData.email} onChange={handleChange} required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-pink-600 focus:ring-2 focus:ring-pink-100 transition"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <input
                type="text" name="subject" value={formData.subject} onChange={handleChange} required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-pink-600 focus:ring-2 focus:ring-pink-100 transition"
                placeholder="Message subject"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <textarea
                name="message" value={formData.message} onChange={handleChange} required rows="5"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-pink-600 focus:ring-2 focus:ring-pink-100 transition resize-none"
                placeholder="Describe your question or issue..."
              ></textarea>
            </div>

            <button
              type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-3 sm:py-4 rounded-xl text-base sm:text-lg font-bold hover:from-pink-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>

          <p className="text-xs sm:text-sm text-gray-500 text-center mt-6">
            🔒 Your data is safe and protected. We never share your information with third parties.
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-8 sm:py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8">
            <div>
              <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <h3 className="text-xl font-extrabold text-white mb-3 cursor-pointer">GrowProPlus</h3>
              </Link>
              <p className="text-gray-400 text-xs sm:text-sm">Agency specialised in social media growth.</p>
            </div>
            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm">Navigation</h4>
              <ul className="space-y-2 text-gray-400 text-xs sm:text-sm">
                <li><Link to="/" className="hover:text-white transition">Home</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm">Legal</h4>
              <ul className="space-y-2 text-gray-400 text-xs sm:text-sm">
                <li><Link to="/terms" className="hover:text-white transition">Terms &amp; Conditions</Link></li>
                <li><Link to="/refund" className="hover:text-white transition">Refund Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-sm">Support</h4>
              <ul className="space-y-2 text-gray-400 text-xs sm:text-sm">
                <li><Link to="/contact" className="hover:text-white transition">Contact Us</Link></li>
                <li><a href="mailto:growproplus@growproplus.com" className="hover:text-white transition">growproplus@growproplus.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-6 text-center">
            <p className="text-gray-400 text-xs sm:text-sm mb-2">© 2026 GrowProPlus. All rights reserved.</p>
            <p className="text-gray-500 text-[10px] sm:text-xs">Not affiliated with any social media platform. We never ask for your password.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
